import type React from 'react'

interface PropertiesPanelProps {
  /** `inline` sits beside the rail on desktop/tablet; `floating` overlays the canvas on mobile. */
  variant: 'inline' | 'floating'
  /** Active tool's label; rendered as "<label> Tool". */
  toolLabel: string
  /** Subtitle describing the current selection. */
  description: string
  scrollContainerRef: React.Ref<HTMLDivElement>
  /** `floating` only: clicking the backdrop beside the panel. */
  onBackdropClick?: () => void
  children: React.ReactNode
}

/** The expanded Properties Panel: tool title, selection subtitle and the scrolling tool content. */
export function PropertiesPanel({
  variant,
  toolLabel,
  description,
  scrollContainerRef,
  onBackdropClick,
  children,
}: PropertiesPanelProps) {
  const header = (
    <div className="p-4 border-b border-border shrink-0">
      <h2 className="text-sm font-medium leading-normal text-foreground">{toolLabel} Tool</h2>
      <p className="mt-1 text-xs leading-normal text-muted-foreground">{description}</p>
    </div>
  )

  if (variant === 'inline') {
    return (
      <div
        className="flex w-[240px] lg:w-[300px] xl:w-[320px] bg-background border-r border-border flex-col h-full overflow-hidden select-none"
        data-left-panel="true"
      >
        {header}
        <div
          ref={scrollContainerRef}
          className="flex-1 overflow-y-auto overflow-x-hidden p-4 min-h-0"
        >
          {children}
        </div>
      </div>
    )
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed z-50 top-[52px] sm:top-[60px] left-12 right-0 bottom-0"
    >
      {/* Backdrop excluding the rail area */}
      <div className="absolute inset-0 bg-black/50" onClick={onBackdropClick} aria-hidden="true" />

      {/* Panel */}
      <div
        className="absolute inset-y-0 left-0 w-[68vw] sm:w-[80vw] md:w-[320px] max-w-[280px] sm:max-w-[360px] md:max-w-[320px] bg-background border-r border-border shadow-lg select-none"
        data-left-panel="true"
      >
        {header}
        <div
          ref={scrollContainerRef}
          className="h-[calc(100%-56px)] overflow-y-auto overflow-x-hidden p-4"
        >
          {children}
        </div>
      </div>
    </div>
  )
}
